/**
 * Serbian Latin → Serbian Cyrillic (ћирилица), for UI text.
 *
 * Serbian's two scripts map one-to-one (Vuk's alphabet: 30 letters each, with lj / nj / dž as
 * single letters), so the Cyrillic UI is derived from the Latin catalog rather than kept as a
 * sixth copy of every string — it can never drift or miss a key.
 *
 * What is NOT transliterated, because Serbian Cyrillic text leaves it in Latin too:
 *   - `{placeholders}` — they are filled with data (names, projects), which is never converted;
 *   - acronyms, codes and identifiers: RTK, MIH, PDF, WGS84, 2xRV07, CanEditAufmass;
 *   - foreign words: anything spelled with q w x y ß ä ö ü, and the domain terms the crews use
 *     as-is (Aufmaß, Bauleiter and its case forms, GeoJSON, GeoPackage …);
 *   - company and brand names: SK Media, XPRO, OpenStreetMap, MapLibre.
 */

const DIGRAPHS: Record<string, string> = {
  Lj: 'Љ', LJ: 'Љ', lj: 'љ', Nj: 'Њ', NJ: 'Њ', nj: 'њ', Dž: 'Џ', DŽ: 'Џ', dž: 'џ',
}

const LETTERS: Record<string, string> = {
  A: 'А', B: 'Б', V: 'В', G: 'Г', D: 'Д', Đ: 'Ђ', E: 'Е', Ž: 'Ж', Z: 'З', I: 'И', J: 'Ј', K: 'К',
  L: 'Л', M: 'М', N: 'Н', O: 'О', P: 'П', R: 'Р', S: 'С', T: 'Т', Ć: 'Ћ', U: 'У', F: 'Ф', H: 'Х',
  C: 'Ц', Č: 'Ч', Š: 'Ш',
  a: 'а', b: 'б', v: 'в', g: 'г', d: 'д', đ: 'ђ', e: 'е', ž: 'ж', z: 'з', i: 'и', j: 'ј', k: 'к',
  l: 'л', m: 'м', n: 'н', o: 'о', p: 'п', r: 'р', s: 'с', t: 'т', ć: 'ћ', u: 'у', f: 'ф', h: 'х',
  c: 'ц', č: 'ч', š: 'ш',
}

/** Words kept in Latin exactly as written (matched case-sensitively, before any suffix rule). */
const KEEP = new Set([
  'Aufmaß', 'Aufmass', 'GeoJSON', 'GeoPackage', 'OpenStreetMap', 'MapLibre', 'Media', 'Slovenia',
  'TopPlus', 'Flurstücke', 'Kataster', 'basemap', 'de', 'Excel', 'Google', 'Chrome', 'Safari',
  // English project-lifecycle terms MIH uses untranslated, and AU's measurement-type codes.
  'Kick', 'off', 'Rollout', 'slic', 'trasse',
  // Place names written in their German form in the text.
  'Bayern', 'Baden',
])

/** Word stems kept in Latin with whatever case ending follows (Bauleitera, Bauleiteru …). */
const KEEP_PREFIX = ['Bauleiter', 'Aufmaß', 'Aufmass']

const isForeign = (w: string) =>
  KEEP.has(w)
  || KEEP_PREFIX.some((p) => w.startsWith(p))
  || /[qwxyßäöüQWXYÄÖÜ0-9]/.test(w)
  || /[a-z][A-Z]/.test(w) // identifiers: CanEditAufmass
  || (w.length >= 2 && w === w.toUpperCase() && /[A-Z]/.test(w))

function word(w: string): string {
  if (isForeign(w)) return w
  let out = ''
  for (let i = 0; i < w.length; i++) {
    const two = w.slice(i, i + 2)
    if (DIGRAPHS[two]) { out += DIGRAPHS[two]; i++; continue }
    const ch = w[i]!
    out += LETTERS[ch] ?? ch
  }
  return out
}

/**
 * Transliterates UI text. Words are runs of letters and digits (so "QGIS-u" is "QGIS" kept +
 * "-у"); `{…}` placeholders and URLs pass through untouched.
 */
export function toCyrillic(text: string): string {
  return text.replace(/(\{[^}]*\}|https?:\/\/\S+|d\.o\.o\.)|([\p{L}\p{N}]+)/gu, (m, keep: string | undefined) =>
    keep ? m : word(m))
}
